
import Vue, { PropType } from 'vue';
import { RunnitNodeField, RUNNIT_NODE_FIELD_TYPE, RunnitNodeRunInputValue, getLogicOnFieldsFromField } from '@run-diffusion/shared';
import { mapState, mapActions } from 'vuex';
import ImageInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/ImageInputField.vue';
import MultiImageInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/MultiImageInputField.vue';
import VideoInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/VideoInputField.vue';
import Asset3DInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/Asset3DInputField.vue';
import _isNil from 'lodash/isNil';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'DynamicMainActionField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
		inputValues: {
			type: Object as PropType<RunnitNodeRunInputValue>,
			required: true,
		},
		hasPromptField: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			RUNNIT_NODE_FIELD_TYPE,
		};
	},
	mixins: [],
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onFieldInput ({ field, value }: { field: RunnitNodeField, value: any }) {
			this.$emit('input', { field, value });
		},
		onGeneratePromptFromImage (imageInput: { nodeRunResult?: any, upload?: any }) {
			this.$emit('generate-prompt-from-image', imageInput);
		},
	},
	computed: {
		...mapState([
			'user',
		]),
		logicOnFields () {
			return getLogicOnFieldsFromField(this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		isShown () {
			const hasField = !!(this.field && (this.field.__rfield || this.field.__rgroup));
			const showsCondition = !this.logicOnFields.allShows.length || this.logicOnFields.triggeredShows.length;
			const adminCondition = !this.field.adminOnly || (this.user && this.user.isAdmin);

			// For autoSet fields, always show the component so autoSet values can be processed
			// The visibility will be controlled by CSS display: none when autoSetVisible: false
			const hasAutoSetLogic = this.field.logic && this.field.logic.field &&
				Object.values(this.field.logic.field).some((logic: any) => logic.autoSet);

			if (hasAutoSetLogic) {
				// Always show autoSet fields so they can process autoSet values
				return !!(hasField && showsCondition && adminCondition);
			}

			// For non-autoSet fields, use the original logic
			return !!(hasField && showsCondition && adminCondition);
		},
		isAutoSetFieldHidden () {
			// Check if this is an autoSet field that should be hidden
			const hasAutoSetLogic = this.field.logic && this.field.logic.field &&
				Object.values(this.field.logic.field).some((logic: any) => logic.autoSet);

			return hasAutoSetLogic && this.logicOnFields.autoSetVisible === false;
		},
		isDisabled () {
			const hasRegularDisables = !!(
				this.isShown &&
				this.logicOnFields.triggeredDisables.length
			);

			// Check if autoSet field is visible but not editable
			const hasAutoSetDisable = !!(
				this.logicOnFields.autoSetValue !== undefined &&
				this.logicOnFields.autoSetVisible &&
				!this.logicOnFields.autoSetAllowEdit
			);

			return hasRegularDisables || hasAutoSetDisable;
		},
		hasAutoSetError () {
			return !!this.logicOnFields.autoSetError;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
	},
	watch: {
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	components: {
		ImageInputField,
		MultiImageInputField,
		VideoInputField,
		Asset3DInputField,
	},
});
